import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import UploadThubmnail from "../../utils/UploadImg";
import {
  ActivateSubscription,
  DeactivateSubscription,
  GetSubscriptionById,
  UpdateSubScrip,
} from "../../services/SubscriptionServices";
import { useSnackbar } from "notistack";
import PlayListDrawer from "./PlayListDrawer";
import { PlayList } from "../content/Playlist";
import {
  sendPutRequest,
  uploadVideoThumbnail,
} from "../../services/ContentServices";
import { TableBodyPlayList } from "../../utils/ContentCardList";

interface Privileges {
  gymMembership: boolean;
  fullContentAccess: boolean;
  customisedDietPlan: boolean;
  playListAccess: boolean;
  onlineMembership: boolean;
}

interface ViewSubscriptionProps {
  setViewSubscription: React.Dispatch<React.SetStateAction<boolean>>;
  setClonedData: React.Dispatch<React.SetStateAction<any>>;
  selectedSubscription: string;
  onClone: () => void;
}
interface FormState {
  thumbnail: File | null;
  name: string;
  currThumbnailUrl: string | null;
  price: number;
  discount: number;
  benefits: string[];
  description: string;
  duration: number;
  maxPausePeriodAllowed: number;
  privileges: Privileges;
  active: boolean;
}

const ViewSubscription: React.FC<ViewSubscriptionProps> = ({
  setViewSubscription,
  setClonedData,
  selectedSubscription,
  onClone,
}) => {
  const theme = useTheme();
  const presetDurations = [30, 60, 90, 180, 270, 366, -1];
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [isCustomDurationSelected, setIsCustomDurationSelected] =
    useState<boolean>(false);
  const [customDurationDays, setCustomDurationDays] = useState<string>("");
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [storedPlayListIds, setStoredPlayListIds] = useState<string[]>([]);
  const [playLists, setPlayLists] = useState<PlayList[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const [initialData, setInitialData] = useState<{
    price: number;
    name: string;
    description: string;
    discount: number;
    previewUrl: string;
    benefits: string[];
    duration: number;
    maxPausePeriodAllowed: number;
  }>({
    price: 0,
    discount: 0,
    name: "",
    description: "",
    previewUrl: "",
    benefits: Array(4).fill(""),
    duration: 30,
    maxPausePeriodAllowed: 0,
  });
  const [formState, setFormState] = useState<FormState>({
    thumbnail: null as File | null,
    name: "",
    currThumbnailUrl: "",
    price: 0,
    discount: 0,
    benefits: Array(4).fill(""),
    description: "",
    duration: 30,
    maxPausePeriodAllowed: 0,
    privileges: {
      gymMembership: false,
      fullContentAccess: false,
      customisedDietPlan: false,
      playListAccess: false,
      onlineMembership: false,
    },
    active: false,
  });

  useEffect(() => {
    const isDisableChanges =
      parseInt(String(formState.price)) == initialData.price &&
      parseInt(String(formState.discount)) == initialData.discount &&
      formState.name == initialData.name &&
      formState.description == initialData.description &&
      formState.currThumbnailUrl == initialData.previewUrl &&
      formState.duration == initialData.duration &&
      formState.maxPausePeriodAllowed == initialData.maxPausePeriodAllowed &&
      formState.benefits.every(
        (benefit, index) => benefit === initialData.benefits[index]
      );
    setCanEdit(isDisableChanges);
  }, [formState, mode]);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      setIsFetching(true);
      const result = await GetSubscriptionById({ id: selectedSubscription });
      setFormState({
        thumbnail: null,
        name: result.name,
        currThumbnailUrl: result.thumbnailUrl || "",
        price: result.price,
        discount: result.discount,
        benefits: result.benefits,
        description: result.shortDescription,
        duration: result.duration > 10000 ? -1 : result.duration,
        maxPausePeriodAllowed: result.maxPausePeriodAllowed || 0,
        privileges: {
          gymMembership: result.gymMembership,
          fullContentAccess: result.hasFullContentAccess,
          customisedDietPlan: result.canRequestDietPlan,
          playListAccess: result.hasAllotedContent,
          onlineMembership: result.onlineMembership,
        },
        active: result.active,
      });
      setInitialData({
        price: result.price,
        discount: result.discount,
        name: result.name,
        description: result.shortDescription,
        previewUrl: result.thumbnailUrl as string,
        benefits: result.benefits,
        duration: result.duration > 10000 ? -1 : result.duration,
        maxPausePeriodAllowed: result.maxPausePeriodAllowed || 0,
      });

      const normalizedDuration = result.duration > 10000 ? -1 : result.duration;
      const isPresetDuration = presetDurations.includes(normalizedDuration);
      setIsCustomDurationSelected(!isPresetDuration);
      setCustomDurationDays(
        !isPresetDuration ? String(normalizedDuration) : ""
      );

      setStoredPlayListIds(result.playLists.map((playlist) => playlist.id));
      setPlayLists(result.playLists as PlayList[]);
      setIsFetching(false);
    } catch (err) {
      setIsFetching(false);
      enqueueSnackbar(
        `Error While Fetching Subscription Details Of ${selectedSubscription}`,
        { variant: "error" }
      );
    }
  };

  const inputStyle = {
    marginTop: theme.spacing(1),
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: "1rem",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: theme.palette.background.default,
    },
  };

  const sectionCardSx = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    p: 2,
  };

  const UpdateSubscription = async () => {
    await fetchSubscription();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,

      [name]: value,
    }));
  };

  const handleNumberChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const parsedValue = Number(value);

    setFormState((prev) => ({
      ...prev,
      [name]: value === "" || Number.isNaN(parsedValue) ? 0 : parsedValue,
    }));
  };

  const durationOptions: Array<{ value: number | "custom"; label: string }> = [
    { value: 30, label: "30 days" },
    { value: 60, label: "60 days" },
    { value: 90, label: "90 days" },
    { value: 180, label: "180 days" },
    { value: 270, label: "270 days" },
    { value: 366, label: "366 days (1 year)" },
    { value: -1, label: "Life time" },
    { value: "custom", label: "Custom" },
  ];

  const handleDurationSelect = (value: number | "custom") => {
    if (mode === "view") {
      return;
    }

    if (value === "custom") {
      setIsCustomDurationSelected(true);
      return;
    }

    setIsCustomDurationSelected(false);
    setCustomDurationDays("");
    setFormState((prev) => ({ ...prev, duration: value }));
  };

  const handleCustomDurationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setCustomDurationDays(value);

    const parsedDuration = Number(value);
    if (!Number.isNaN(parsedDuration) && parsedDuration > 0) {
      setFormState((prev) => ({ ...prev, duration: parsedDuration }));
    }
  };

  const resetForm = () => {
    setFormState((prev) => ({
      ...prev,
      price: initialData.price,
      discount: initialData.discount,
      name: initialData.name,
      description: initialData.description,
      currThumbnailUrl: initialData.previewUrl,
      benefits: initialData.benefits,
      duration: initialData.duration,
      maxPausePeriodAllowed: initialData.maxPausePeriodAllowed,
    }));

    const isPresetDuration = presetDurations.includes(initialData.duration);
    setIsCustomDurationSelected(!isPresetDuration);
    setCustomDurationDays(
      !isPresetDuration ? String(initialData.duration) : ""
    );
  };

  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formState.name) {
      return enqueueSnackbar("Please provide the name of the subscription");
    }
    if (!formState.price) {
      return enqueueSnackbar("Please provide the price.");
    }
    if (!formState.description) {
      return enqueueSnackbar("Please provide a description.");
    }

    if (isCustomDurationSelected) {
      const customDays = Number(customDurationDays);
      if (!customDurationDays || Number.isNaN(customDays) || customDays < 1) {
        return enqueueSnackbar("Custom duration must be at least 1 day", {
          variant: "warning",
        });
      }
    }

    if (formState.maxPausePeriodAllowed < 0) {
      return enqueueSnackbar("Max pause period cannot be negative", {
        variant: "warning",
      });
    }

    if (!formState.thumbnail && !formState.currThumbnailUrl) {
      enqueueSnackbar("Thumbnail is required", { variant: "warning" });
      return;
    }
    setIsUploading(true);
    let thumbAwsUrl = formState.currThumbnailUrl || "";
    if (formState.thumbnail) {
      const signUrlResponse = await uploadVideoThumbnail(
        formState.thumbnail.name
      );
      const signUrl = signUrlResponse.data?.data as string;
      thumbAwsUrl = signUrl.split("?")[0];
      const upload = await sendPutRequest({
        signUrl,
        file: formState.thumbnail,
      });
      if (upload.status !== 200) {
        enqueueSnackbar("Failed to upload thumbnail", { variant: "error" });
        setIsUploading(false);
        return;
      }
    }

    const Status = await UpdateSubScrip({
      id: selectedSubscription,
      price: formState.price,
      discount: formState.discount,
      shortDescription: formState.description,
      name: formState.name,
      thumbnailUrl: thumbAwsUrl as string,
      benefits: formState.benefits,
      duration: formState.duration,
      maxPausePeriodAllowed: formState.maxPausePeriodAllowed,
    });
    if (Status == 200) {
      enqueueSnackbar("Subscription Updated Successfully.");
      UpdateSubscription();
      setMode("view");
      setIsUploading(false);
    } else {
      enqueueSnackbar("Failed to Edit Subscription", { variant: "error" });
      setIsUploading(false);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    const updatedPrivileges = { ...formState.privileges };

    if (name === "gymMembership") {
      updatedPrivileges.gymMembership = checked;
      updatedPrivileges.fullContentAccess = checked;
      updatedPrivileges.customisedDietPlan = checked;
      updatedPrivileges.playListAccess = checked;
    } else if (name === "fullContentAccess") {
      updatedPrivileges.fullContentAccess = checked;
      if (checked) {
        updatedPrivileges.playListAccess = true;
      } else {
        updatedPrivileges.playListAccess = false;
      }
    } else {
      updatedPrivileges[name as keyof typeof updatedPrivileges] = checked;
    }

    setFormState((prev) => ({
      ...prev,
      privileges: updatedPrivileges,
    }));
  };

  const handleDeactivation = async () => {
    setIsUploading(true);
    const status = await DeactivateSubscription({
      id: selectedSubscription,
    });

    if (status == 204) {
      enqueueSnackbar(`Subscription Deactivated Successfully!!`, {
        variant: "warning",
      });
      UpdateSubscription();
    } else {
      enqueueSnackbar(`Subscription Deactivation Failed!!`, {
        variant: "error",
      });
    }
    setIsUploading(false);
  };

  const handleactivation = async () => {
    const status = await ActivateSubscription({
      id: selectedSubscription,
    });

    if (status == 204) {
      setIsUploading(true);
      enqueueSnackbar(`Subscription Activated Successfully!!`, {
        variant: "success",
      });
      UpdateSubscription();
    } else {
      enqueueSnackbar(`Subscription Activation Failed!!`, {
        variant: "error",
      });
    }
    setIsUploading(false);
  };

  const openDrawer = () => {
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const handleClone = () => {
    setViewSubscription(false);
    onClone();
    setClonedData({
      id: selectedSubscription,
      name: formState.name,
      price: formState.price,
      duration: formState.duration,
      benefits: formState.benefits,
      shortDescription: formState.description,
      thumbnailUrl: formState.currThumbnailUrl,
      hasFullContentAccess: formState.privileges.fullContentAccess,
      canRequestDietPlan: formState.privileges.customisedDietPlan,
      playLists: playLists,
      discount: formState.discount,
      maxPausePeriodAllowed: formState.maxPausePeriodAllowed,
      newSubscriptionId: "",
      active: false,
      gymMembership: formState.privileges.gymMembership,
      hasAllotedContent: formState.privileges.playListAccess,
      onlineMembership: formState.privileges.onlineMembership,
    });
  };
  const benefitsToRender =
    formState.benefits.length >= 4
      ? formState.benefits
      : [
          ...formState.benefits,
          ...Array(4 - formState.benefits.length).fill(""),
        ];

  return (
    <Box sx={{ p: 1 }}>
      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
        <IconButton
          edge="start"
          aria-label="back"
          onClick={() => {
            if (mode === "edit") {
              resetForm();
              setMode("view");
            } else {
              setViewSubscription(false);
            }
          }}
          sx={{ marginRight: theme.spacing(1), marginLeft: theme.spacing(1) }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h6">
          {mode === "edit" ? "Edit Subscription" : "Subscription Details"}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField
              id="name"
              variant="outlined"
              name="name"
              placeholder="Enter Subscription Name"
              type="text"
              sx={inputStyle}
              disabled={mode === "view"}
              required
              fullWidth
              value={formState.name}
              onChange={handleInputChange}
            />
            <TextField
              id="price"
              variant="outlined"
              name="price"
              placeholder="Enter Price"
              type="number"
              sx={inputStyle}
              required
              disabled={mode === "view"}
              fullWidth
              value={formState.price !== 0 ? formState.price : ""}
              onChange={handleNumberChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <b style={{ color: mode === "view" ? "gray" : "white" }}>
                      ₹
                    </b>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="discount"
              variant="outlined"
              type="number"
              name="discount"
              placeholder="Enter Discount"
              disabled={mode === "view"}
              sx={inputStyle}
              required
              fullWidth
              value={formState.discount !== 0 ? formState.discount : ""}
              onChange={handleNumberChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <b style={{ color: mode === "view" ? "gray" : "white" }}>
                      %
                    </b>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="description"
              variant="outlined"
              name="description"
              sx={inputStyle}
              multiline
              rows={11}
              disabled={mode === "view"}
              placeholder="Enter Short Description"
              fullWidth
              value={formState.description}
              onChange={handleInputChange}
            />
            <UploadThubmnail
              previewUrl={formState.currThumbnailUrl as string}
              disabled={mode === "view"}
              setPreviewUrl={(url) =>
                setFormState((prev) => ({
                  ...prev,
                  currThumbnailUrl: url as string,
                }))
              }
              setFile={(file) =>
                setFormState((prev) => ({
                  ...prev,
                  thumbnail: (file as File) || null,
                }))
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={sectionCardSx}>
              <Typography fontSize={26} fontWeight={600} sx={{ mb: 1.5 }}>
                Duration
              </Typography>

              <Grid container spacing={1.5}>
                {durationOptions.map((option) => {
                  const selected =
                    option.value === "custom"
                      ? isCustomDurationSelected
                      : !isCustomDurationSelected &&
                        formState.duration === option.value;

                  return (
                    <Grid item xs={6} key={option.label}>
                      <Button
                        fullWidth
                        onClick={() => handleDurationSelect(option.value)}
                        disabled={mode === "view"}
                        sx={{
                          borderRadius: "10px",
                          p: 1.4,
                          textTransform: "none",
                          color: selected
                            ? theme.palette.background.default
                            : theme.palette.text.primary,
                          fontWeight: 700,
                          backgroundColor: selected
                            ? theme.palette.secondary.main
                            : theme.palette.background.paper,
                          border: `1px solid ${theme.palette.secondary.main}`,
                          "&:hover": {
                            backgroundColor: selected
                              ? theme.palette.secondary.main
                              : theme.palette.info.light,
                          },
                        }}
                      >
                        {option.label}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>

              {isCustomDurationSelected && (
                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    placeholder="Enter custom number of days"
                    type="number"
                    fullWidth
                    disabled={mode === "view"}
                    value={customDurationDays}
                    onChange={handleCustomDurationChange}
                    sx={inputStyle}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.8,
                      ml: 0.2,
                    }}
                  >
                    Enter a custom duration in days
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={sectionCardSx}>
              <Typography fontSize={24} fontWeight={600} sx={{ mb: 1.5 }}>
                Max Pause Period
              </Typography>
              <TextField
                placeholder="Enter maximum pause period (days)"
                type="number"
                name="maxPausePeriodAllowed"
                fullWidth
                disabled={mode === "view"}
                value={
                  formState.maxPausePeriodAllowed !== 0
                    ? formState.maxPausePeriodAllowed
                    : ""
                }
                onChange={handleNumberChange}
                sx={inputStyle}
              />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mt: 0.8, ml: 0.2 }}
              >
                Maximum days subscription can be paused
              </Typography>
            </Box>

            <Box sx={sectionCardSx}>
              <Typography fontSize={24} fontWeight={600} sx={{ mb: 1.5 }}>
                Subscription Benefits
              </Typography>
              {benefitsToRender.map((benefit, index) => (
                <TextField
                  key={index}
                  id={`benefit${index + 1}`}
                  variant="outlined"
                  name={`benefit${index}`}
                  placeholder={`Enter benefit ${index + 1}`}
                  sx={{ ...inputStyle, mt: index === 0 ? 0 : 1 }}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LoyaltyIcon
                          sx={{
                            fontSize: 15,
                            color:
                              mode === "view"
                                ? theme.palette.text.secondary
                                : theme.palette.secondary.main,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  disabled={mode === "view"}
                  value={benefit}
                  onChange={(e) => {
                    const updatedBenefits = [...benefitsToRender];
                    updatedBenefits[index] = e.target.value;
                    setFormState((prev) => ({
                      ...prev,
                      benefits: updatedBenefits,
                    }));
                  }}
                />
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              {mode === "edit" && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={resetForm}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleSaveChanges}
                    color="secondary"
                    disabled={canEdit || isUploading}
                  >
                    {isUploading ? (
                      <CircularProgress
                        style={{ width: "20px", height: "20px" }}
                        color="inherit"
                      />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </>
              )}

              {mode === "view" && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setMode("edit")}
                  >
                    Edit
                  </Button>
                  {formState.active ? (
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={isUploading}
                      onClick={handleDeactivation}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleClone}
                      >
                        Clone
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        disabled={isUploading}
                        onClick={handleactivation}
                      >
                        Activate
                      </Button>
                    </>
                  )}
                </>
              )}
            </Box>

            <Box sx={sectionCardSx}>
              <Typography fontSize={24} fontWeight={600} sx={{ mb: 1.3 }}>
                Privileges
              </Typography>
              <FormGroup
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                {Object.keys(formState.privileges).map((privilege) => (
                  <Box key={privilege} sx={{ display: "flex", width: "100%" }}>
                    <FormControlLabel
                      sx={{ width: "100%", mt: 0.4 }}
                      disabled
                      control={
                        <Checkbox
                          checked={
                            formState.privileges[
                              privilege as keyof typeof formState.privileges
                            ]
                          }
                          name={privilege}
                          disabled={false}
                        />
                      }
                      label={privilege
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    />
                  </Box>
                ))}
              </FormGroup>
            </Box>

            <Box sx={{ ...sectionCardSx, minHeight: 220 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography fontSize={24} fontWeight={600}>
                  Playlists
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={
                    formState.privileges.gymMembership ||
                    formState.privileges.fullContentAccess ||
                    !formState.privileges.playListAccess
                  }
                  onClick={openDrawer}
                >
                  Manage Playlist
                </Button>
              </Box>

              <PlayListDrawer
                open={drawerOpen}
                onClose={closeDrawer}
                selectedSubscription={selectedSubscription}
                storedPlayListIds={storedPlayListIds}
                onDataUpdate={UpdateSubscription}
              />

              <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
              <Box
                sx={{
                  maxHeight: "42vh",
                  overflowY: "auto",
                  scrollbarWidth: "none",
                  flexGrow: 1,
                }}
              >
                <TableBodyPlayList
                  loading={isFetching}
                  playlists={playLists}
                  hasContentAccess={formState.privileges.fullContentAccess}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export { ViewSubscription };
